import { tokenManager } from "@babackapp/auth-client/utils";
import useGenerateLabel from "@babackapp/private-api-client/client/use-generate-label";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, buttonVariants } from "@babackapp/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { DownloadIcon, FileIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import {
  AlertTriangleIcon,
  Building2,
  Building2Icon,
  ClockIcon,
  FilePlus,
  FileWarningIcon,
  LoaderIcon,
  Pencil,
  ScanBarcodeIcon,
  TruckIcon,
} from "lucide-react";
import { useRef, useState } from "react";
import { useViewerDateFnsLocale } from "@/lib/translator";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import CopyBtn from "@/components/copy-btn";
import ReturnRequestCarrierSelectionModal from "@/components/return-request-carrier-selection-modal";
import ReturnRequestPickupTimeModal from "@/components/return-request-pickup-time-modal";
import { cn } from "@/utils/cn";

type ShippingInfoProps = {
  returnRequest: ReturnRequestDto;
};

export default function ShippingInfo(props: ShippingInfoProps) {
  const dateFnsLocale = useViewerDateFnsLocale();
  const t = useTranslation();
  const {
    returnRequest: {
      shipping_info: {
        tracking_code: trackingCode,
        logisticCode,
        carrier,
        carrierTitle,
        returnStoreId,
        pickingTiming,
      },
    },
  } = props;

  const code =
    trackingCode && trackingCode !== "" ? trackingCode : logisticCode;
  const pickingTime =
    pickingTiming && pickingTiming.pickupTimeStart > 0 ? pickingTiming : null;

  const carrierEnhancedTitle =
    carrier === "no-physical-return"
      ? t("carrier.noPhysicalReturn")
      : carrierTitle;

  return (
    <div className="">
      {carrier === "return-to-store" && returnStoreId ? (
        <ReturnToStore returnStoreId={returnStoreId} />
      ) : null}
      <Carrier
        carrier={carrierEnhancedTitle || carrier}
        code={code}
        returnRequest={props.returnRequest}
      />
      {carrier !== "no-physical-return" && (
        <>
          <GenerateLabelButton returnRequest={props.returnRequest} />
          <CarrierError returnRequest={props.returnRequest} />
          <LabelFile returnRequest={props.returnRequest} />

          {pickingTime && (
            <PickupTimeDisplay
              pickingTime={pickingTime}
              returnRequest={props.returnRequest}
            />
          )}
        </>
      )}
      <div className="text-sm font-regular text-gray-900"></div>
    </div>
  );
}

function ReturnToStore({ returnStoreId }: { returnStoreId: string }) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const store =
    shop.returnToStore.stores.find((s) => s.id === returnStoreId) ??
    shop.returnToStore.stores[0];

  if (!store) return null;

  return (
    <div className="rounded border p-2 my-2 flex flex-col gap-1">
      <span className="inline-flex gap-1 text-xs items-center text-muted-foreground">
        <Building2Icon className="size-3" />{" "}
        {t("returnDetail.newReturnRequestReturnToStore")}
      </span>
      {store ? (
        <span className="text-sm font-medium">
          {store.name || store.address.companyName}
        </span>
      ) : (
        <Building2 className="size-4" />
      )}
    </div>
  );
}

type LabelFileProps = {
  returnRequest: ReturnRequestDto;
};

function LabelFile({ returnRequest }: LabelFileProps) {
  const t = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const labelUrl = returnRequest.shipping_info.label_url;

  if (!labelUrl) return null;

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const accessToken = tokenManager.getAccessToken();
      const response = await fetch(labelUrl, {
        credentials: "include",
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : undefined,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch label");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `label-${returnRequest.shipping_info.tracking_code || returnRequest.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading label:", error);
      // Fallback to direct link if fetch fails
      window.open(labelUrl, "_blank");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-md border p-2 my-2 flex gap-1">
      <span className="inline-flex gap-1 text-xs items-center text-muted-foreground flex-1">
        <FileIcon className="size-4" />
        <div className="flex flex-col">
          <span className="text-xs text-foreground">
            {t("returnDetail.rightBar.carrierLabel")}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {t("returnDetail.rightBar.clickToView")}
          </span>
        </div>
      </span>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isLoading}
        className={cn(
          buttonVariants({
            size: "sm",
            variant: "outline",
          }),
          "w-6 h-6 flex hover:scale-[1.1] active:scale-[0.8] transition-all ease-in-out",
        )}
      >
        {isLoading ? (
          <LoaderIcon className="size-4 animate-spin" />
        ) : (
          <DownloadIcon className="size-4" />
        )}
      </button>
    </div>
  );
}

type CarrierProps = {
  carrier: string;
  code: string;
  returnRequest: ReturnRequestDto;
};

function Carrier({ carrier, code, returnRequest }: CarrierProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = useTranslation();
  const isShipped = !!returnRequest.status.shipped.done;

  return (
    <>
      <div className="rounded border p-2 my-2 flex flex-col">
        {carrier !== "return-to-store" && (
          <div className="flex items-center justify-between">
            <span className="inline-flex gap-1 text-xs items-center text-foreground">
              <TruckIcon className="size-3" />
              <div className="text-xs">{carrier}</div>
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    size="sm"
                    variant="outline"
                    disabled={isShipped}
                    className={cn(
                      "w-6 h-6 flex hover:scale-[1.1] active:scale-[0.9] transition-all ease-in-out",
                    )}
                  >
                    <Pencil className="size-4" />
                  </Button>
                </div>
              </TooltipTrigger>
              {isShipped && (
                <TooltipContent className="bg-popover text-popover-foreground border">
                  <p>{t("returnDetail.carrierCannotBeEditedAfterShipped")}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        )}
        {code && (
          <div className="group flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <CopyBtn
                value={code}
                className="border-none px-0 text-xs text-muted-foreground py-0"
              >
                <span
                  className="text-sm font-regular truncate block max-w-[180px]"
                  title={code}
                >
                  {code}
                </span>
              </CopyBtn>
            </div>
          </div>
        )}
      </div>

      <ReturnRequestCarrierSelectionModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        returnRequest={returnRequest}
        validationButtonLabel="returnDetail.update.modify"
      />
    </>
  );
}

type PickupTimeDisplayProps = {
  pickingTime: NonNullable<ReturnRequestDto["shipping_info"]["pickingTiming"]>;
  returnRequest: ReturnRequestDto;
};

function PickupTimeDisplay({
  pickingTime,
  returnRequest,
}: PickupTimeDisplayProps) {
  const t = useTranslation();
  const dateFnsLocale = useViewerDateFnsLocale();
  const [isPickupTimeModalOpen, setIsPickupTimeModalOpen] = useState(false);
  const isShipped = !!returnRequest.status.shipped.done;

  return (
    <>
      <div className="rounded border p-2 my-2 flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs">
            {t("returnDetail.rightBar.pickupAsked")}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  onClick={() => setIsPickupTimeModalOpen(true)}
                  size="sm"
                  variant="outline"
                  disabled={isShipped}
                  className={cn(
                    "w-6 h-6 flex hover:scale-[1.1] active:scale-[0.9] transition-all ease-in-out",
                  )}
                >
                  <ClockIcon className="size-4" />
                </Button>
              </div>
            </TooltipTrigger>
            {isShipped && (
              <TooltipContent className="bg-popover text-popover-foreground border">
                <p>{t("returnDetail.pickupTimeCannotBeEditedAfterShipped")}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>
        <div className="text-sm space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">
              {format(pickingTime.pickupTimeStart, "P", {
                locale: dateFnsLocale,
              })}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("returns.of")}
            </span>
            <span className="text-gray-500">
              {format(pickingTime.pickupTimeStart, "p", {
                locale: dateFnsLocale,
              })}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("returnDetail.rightBar.to")}
            </span>
            <span className="text-gray-500">
              {format(pickingTime.pickupTimeEnd, "p", {
                locale: dateFnsLocale,
              })}
            </span>
          </div>
        </div>
      </div>

      <ReturnRequestPickupTimeModal
        open={isPickupTimeModalOpen}
        onOpenChange={setIsPickupTimeModalOpen}
        returnRequest={returnRequest}
      />
    </>
  );
}

function parseCarrierErrorDetail(raw: string): string {
  // errorDetail is stored as "{carrierCode} {message}" — strip the carrier prefix
  const withoutCarrier = raw.replace(/^[a-z0-9-]+ /i, "");

  // Legacy format: the raw JSON body was stringified and stored directly.
  // Try to extract human-readable messages from it.
  try {
    const parsed: unknown = JSON.parse(withoutCarrier);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "response" in parsed &&
      parsed.response !== null &&
      typeof parsed.response === "object" &&
      "errors" in parsed.response &&
      Array.isArray(parsed.response.errors)
    ) {
      return parsed.response.errors
        .map((e: { code?: string; message?: string }) =>
          e.message && e.code
            ? `${e.message} (${e.code})`
            : (e.message ?? String(e)),
        )
        .join(", ");
    }
  } catch {
    // Not JSON — use as-is
  }

  return withoutCarrier;
}

function CarrierError({ returnRequest }: { returnRequest: ReturnRequestDto }) {
  const lastCarrierError = [...returnRequest.logs]
    .reverse()
    .find((log) => log.type === "error" && log.errorType === "carrier");

  if (!lastCarrierError?.errorDetail) return null;

  const detail = parseCarrierErrorDetail(lastCarrierError.errorDetail);

  return (
    <div className="rounded border border-orange-200 bg-orange-50 p-2 my-2 flex gap-2 items-start">
      <AlertTriangleIcon className="size-3.5 text-orange-500 mt-0.5 shrink-0" />
      <p className="text-xs text-orange-700 leading-snug">{detail}</p>
    </div>
  );
}

type GenerateLabelButtonProps = {
  returnRequest: ReturnRequestDto;
};

function GenerateLabelButton({ returnRequest }: GenerateLabelButtonProps) {
  const t = useTranslation();
  const {
    mutateAsync: generateLabel,
    isPending,
    isError,
  } = useGenerateLabel({
    onError: () => {
      document.querySelector("#return-request-activities")?.scrollIntoView({
        behavior: "smooth",
      });
    },
    onSuccess: (data) => {
      if (!data.shipping_info.label_url) throw new Error("no label generated");
      const a = document.createElement("a");
      a.href = data.shipping_info.label_url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
  });
  const labelUrl = returnRequest.shipping_info.label_url;
  const linkRef = useRef<HTMLAnchorElement>(null);

  const onDownload = async () => {
    if (labelUrl) return;

    try {
      const {
        shipping_info: { label_url },
      } = await generateLabel({
        shopId: returnRequest.shopId,
        returnRequestId: returnRequest.id,
      });
      if (!label_url) throw new Error("no label generated");
      linkRef.current?.setAttribute("href", label_url);
      linkRef.current?.click();
    } catch (e) {
      console.error(e);
      return;
    }
  };

  if (labelUrl) {
    return null;
  }

  return (
    <button
      onClick={onDownload}
      disabled={isPending}
      className="rounded-md group border p-2 my-2 flex items-center cursor-pointer gap-1 shadow w-full"
    >
      <span className="inline-flex gap-1 text-xs items-center text-muted-foreground flex-1">
        {isError ? (
          <FileWarningIcon className="text-destructive size-4" />
        ) : (
          <FilePlus className="size-4" />
        )}
        <div className="flex flex-col">
          <span className="text-xs text-foreground">
            {t("returnDetail.rightBar.carrierLabel")}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {t("returnDetail.rightBar.generateLabel")}
          </span>
        </div>
      </span>
      <div
        className={cn(
          buttonVariants({ variant: "default", size: "icon" }),
          "w-6 h-6 flex group-hover:scale-[1.1] group-active:scale-[0.9] shadow bg-primary text-primary-foreground transition-all ease-in-out",
        )}
      >
        {isPending ? (
          <LoaderIcon className="animate-spin" />
        ) : (
          <ScanBarcodeIcon className="size-4" />
        )}
      </div>
    </button>
  );
}
